import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  User,
  Shield,
  ShieldCheck,
  Eye,
  Pencil,
  Trash2,
  Search,
  MoreVertical,
  Crown,
  Bot,
  UserCog,
  UserX,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ROLES = [
  { value: "super_admin", label: "Super Admin", icon: Crown, color: "text-amber-400 bg-amber-950/30 border-amber-900/50" },
  { value: "admin", label: "Admin", icon: ShieldCheck, color: "text-purple-400 bg-purple-950/30 border-purple-900/50" },
  { value: "bot_creator", label: "Bot Creator", icon: Bot, color: "text-blue-400 bg-blue-950/30 border-blue-900/50" },
  { value: "editor", label: "Editor", icon: Pencil, color: "text-emerald-400 bg-emerald-950/30 border-emerald-900/50" },
  { value: "viewer", label: "Viewer", icon: Eye, color: "text-zinc-400 bg-zinc-800 border-zinc-700" },
];

const getRoleConfig = (role) => ROLES.find(r => r.value === role) || ROLES[4];

export default function Users({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "viewer", status: "active", two_factor_enabled: false });
  const [loading, setLoading] = useState(false);

  const canManageUsers = currentUser?.role === "super_admin" || currentUser?.role === "admin";

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data.users);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.role?.toLowerCase().includes(search.toLowerCase()) ||
    user.status?.toLowerCase().includes(search.toLowerCase())
  );

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/users", newUser);
      toast.success("User created successfully!");
      setShowCreateUser(false);
      setNewUser({ name: "", email: "", password: "", role: "viewer", status: "active", two_factor_enabled: false });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status || "active",
      password: "",
      two_factor_enabled: user.two_factor_enabled || false
    });
    setShowEditUser(true);
  };

  const toggleUserStatus = async (user) => {
    const newStatus = user.status === "inactive" ? "active" : "inactive";
    try {
      await api.put(`/users/${user.id}`, { status: newStatus });
      toast.success(`User ${newStatus === "active" ? "activated" : "deactivated"}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    
    setLoading(true);
    try {
      const updateData = {};
      if (editingUser.name) updateData.name = editingUser.name;
      if (editingUser.email) updateData.email = editingUser.email;
      if (editingUser.role) updateData.role = editingUser.role;
      if (editingUser.password) updateData.password = editingUser.password;
      
      await api.put(`/users/${editingUser.id}`, updateData);
      toast.success("User updated successfully!");
      setShowEditUser(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-zinc-500 text-sm">Manage platform users and permissions</p>
        </div>
        {canManageUsers && (
          <Button onClick={() => setShowCreateUser(true)} data-testid="create-user-btn" className="bg-white text-black hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {ROLES.map(role => {
          const count = users.filter(u => u.role === role.value).length;
          const Icon = role.icon;
          return (
            <Card key={role.value} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${role.color.split(' ').slice(1).join(' ')}`}>
                    <Icon className={`w-4 h-4 ${role.color.split(' ')[0]}`} />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{count}</p>
                    <p className="text-xs text-zinc-500">{role.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Search users by name, email, or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="user-search-input"
        />
      </div>

      {/* Users Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="border-b border-zinc-800 py-3">
          <CardTitle className="text-sm font-medium text-zinc-400">
            All Users ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                <TableHead className="text-xs uppercase text-zinc-500 py-3">User</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Email</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Role</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Status</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">2FA</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Created</TableHead>
                {canManageUsers && (
                  <TableHead className="text-xs uppercase text-zinc-500 py-3 text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageUsers ? 7 : 6} className="text-center py-12 text-zinc-500">
                    <UserCog className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No users found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const roleConfig = getRoleConfig(user.role);
                  const RoleIcon = roleConfig.icon;
                  const isCurrentUser = user.id === currentUser?.id;
                  const isInactive = user.status === "inactive";
                  
                  return (
                    <TableRow key={user.id} className={`border-b border-zinc-800/50 hover:bg-zinc-900/50 ${isInactive ? 'opacity-60' : ''}`}>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isInactive ? 'bg-zinc-800/50' : 'bg-zinc-800'}`}>
                            <User className="w-4 h-4 text-zinc-400" />
                          </div>
                          <div>
                            <span className="font-medium text-sm flex items-center gap-2">
                              {user.name}
                              {isCurrentUser && (
                                <Badge variant="outline" className="text-xs bg-emerald-950/30 text-emerald-400 border-emerald-900/50">
                                  You
                                </Badge>
                              )}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-sm text-zinc-400">{user.email}</TableCell>
                      <TableCell className="py-3">
                        <Badge variant="outline" className={`${roleConfig.color} text-xs`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${isInactive 
                            ? 'bg-red-950/30 text-red-400 border-red-900/50' 
                            : 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50'}`}
                        >
                          {isInactive ? <UserX className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                          {isInactive ? 'Inactive' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3">
                        {user.two_factor_enabled ? (
                          <Badge variant="outline" className="text-xs bg-blue-950/30 text-blue-400 border-blue-900/50">
                            Enabled
                          </Badge>
                        ) : (
                          <span className="text-xs text-zinc-600">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-zinc-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      {canManageUsers && (
                        <TableCell className="py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-menu-${user.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                              <DropdownMenuItem onClick={() => startEditUser(user)} className="cursor-pointer">
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {!isCurrentUser && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => toggleUserStatus(user)} 
                                    className="cursor-pointer"
                                  >
                                    {isInactive ? (
                                      <>
                                        <UserCheck className="w-4 h-4 mr-2 text-emerald-400" />
                                        <span className="text-emerald-400">Activate</span>
                                      </>
                                    ) : (
                                      <>
                                        <UserX className="w-4 h-4 mr-2 text-amber-400" />
                                        <span className="text-amber-400">Deactivate</span>
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-zinc-800" />
                                  <DropdownMenuItem 
                                    onClick={() => deleteUser(user.id, user.name)} 
                                    className="text-red-400 cursor-pointer"
                                    disabled={user.role === "super_admin" && currentUser?.role !== "super_admin"}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Create New User</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Add a new user to the platform with a specific role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Full Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@company.io"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Enter password"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Role *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="new-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {ROLES.map(role => {
                    const Icon = role.icon;
                    return (
                      <SelectItem key={role.value} value={role.value} className="cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${role.color.split(' ')[0]}`} />
                          {role.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-600 mt-2">
                <strong>Super Admin:</strong> Full access, manage all users<br />
                <strong>Admin:</strong> Can create and manage users<br />
                <strong>Bot Creator:</strong> Can create and manage bots<br />
                <strong>Editor:</strong> Can edit bot configurations<br />
                <strong>Viewer:</strong> Read-only access
              </p>
            </div>
            
            {/* 2FA Toggle - Super Admin only */}
            {currentUser?.role === "super_admin" && (
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                <div>
                  <Label className="text-zinc-300">Two-Factor Authentication</Label>
                  <p className="text-xs text-zinc-500 mt-1">Require 2FA verification on login</p>
                </div>
                <Switch
                  checked={newUser.two_factor_enabled}
                  onCheckedChange={(checked) => setNewUser({ ...newUser, two_factor_enabled: checked })}
                  data-testid="new-user-2fa"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateUser(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button onClick={createUser} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="create-user-submit">
              {loading ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit User</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Full Name</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Email</Label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="john@company.io"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <Label className="text-zinc-400">New Password</Label>
                <Input
                  type="password"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-user-password"
                />
                <p className="text-xs text-zinc-600 mt-1">Leave blank to keep existing password</p>
              </div>
              <div>
                <Label className="text-zinc-400">Role</Label>
                <Select value={editingUser.role} onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="edit-user-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ROLES.map(role => {
                      const Icon = role.icon;
                      return (
                        <SelectItem key={role.value} value={role.value} className="cursor-pointer">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${role.color.split(' ')[0]}`} />
                            {role.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Status</Label>
                <Select value={editingUser.status} onValueChange={(value) => setEditingUser({ ...editingUser, status: value })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="edit-user-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="active" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-emerald-400" />
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="inactive" className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <UserX className="w-4 h-4 text-red-400" />
                        Inactive
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-600 mt-1">Inactive users cannot log in</p>
              </div>
              
              {/* 2FA Toggle - Super Admin only */}
              {currentUser?.role === "super_admin" && (
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                  <div>
                    <Label className="text-zinc-300">Two-Factor Authentication</Label>
                    <p className="text-xs text-zinc-500 mt-1">Require 2FA verification on login</p>
                  </div>
                  <Switch
                    checked={editingUser.two_factor_enabled}
                    onCheckedChange={(checked) => setEditingUser({ ...editingUser, two_factor_enabled: checked })}
                    data-testid="edit-user-2fa"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditUser(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button onClick={saveEditUser} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="edit-user-submit">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
